import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { EstimagePayApplication, EstimagePayApplicationStatus } from './estimage-pay-application.model';
import { ResponseEnumEnum } from '../../../enums/responseEnum.enum';
import { ToolbarService } from '../../../services/toolbar.service';
import { RepairerAdminService } from '../../../services/repairer-admin.service';
import { DynamicDialogService } from '../../../components/dynamic-dialog/dynamic-dialog-service';
import { UserService } from '../../../services/user.service';
import { HttpErrorResponse } from '@angular/common/http';

export type EstimagePayApplicationState = {
  isLoading: boolean;
  status: EstimagePayApplicationStatus;
  onboardingUrl: string;
  flipMoneyId: string;
};

const initialState: EstimagePayApplicationState = {
  isLoading: true,
  status: null,
  onboardingUrl: null,
  flipMoneyId: null
};

@Injectable()
export class EstimagePayApplicationStore extends ComponentStore<EstimagePayApplicationState> {
  toolbarService = inject(ToolbarService);
  userService = inject(UserService);
  repairerAdminService = inject(RepairerAdminService);
  dynamicDialogService = inject(DynamicDialogService);

  isLoading = this.selectSignal((state) => state.isLoading);
  status = this.selectSignal((state) => state.status);
  onboardingUrl = this.selectSignal((state) => state.onboardingUrl);
  flipMoneyId = this.selectSignal((state) => state.flipMoneyId);

  constructor() {
    super(initialState);
    this.getEstImagePayApplication();
  }

  getEstImagePayApplication = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() => {
        this.toolbarService.setCloudState('FETCHING');
        this.patchState({ isLoading: true });
      }),
      switchMap(() => {
        return this.userService.getEstimagePayApplication().pipe(
          tapResponse({
            next: (application: EstimagePayApplication) => this.patchState({ isLoading: false, ...application }),
            error: (err: HttpErrorResponse) => console.error({ err }),
            finalize: () => {
              this.toolbarService.setCloudState('RESTING');
              this.patchState({ isLoading: false });
            }
          })
        );
      })
    );
  });

  applyToEstImage = this.effect((trigger$: Observable<string>) => {
    return trigger$.pipe(
      tap(() => {
        this.toolbarService.setCloudState('SAVING');
      }),
      switchMap((email: string) => {
        return this.repairerAdminService.applyRepairerSiteToEstimagePay(email).pipe(
          tapResponse({
            next: ({ onboardingUrl }) => {
              this.dynamicDialogService.closeDialog(ResponseEnumEnum.SUCCESS, 'Application sent to EstImage Pay');
              this.patchState({ status: 'PENDING', onboardingUrl });
            },
            error: () =>
              this.dynamicDialogService.closeDialog(ResponseEnumEnum.ERROR, 'Could not apply to EstImage Pay')
          })
        );
      })
    );
  });
}
